/**
 * Whether a page gets search traffic, and what the delete and merge advice may say because of it.
 *
 * The owner's rule (quality sprint 3, Q2): never advise deleting or merging a page that gets search
 * traffic. What counts as traffic depends on what we know about the site:
 *
 * - **Search Console connected** (a connection that covers the domain and has synced data): any
 *   Search Console click in the summary window (90 days). The crawl estimate is not consulted: on a
 *   connected site Search Console is the record, and the estimate is a model of it. For delete and
 *   merge advice a page Google shows often is not idle either, clicks or not
 *   (`pageSeenInSearch`, quality sprint 7 D).
 * - **Not connected:** any estimated visits, or any ranked keyword. Also an estimate above 0 in the
 *   last crawl before DataForSEO changed how it estimates traffic (1 November 2026), when the current
 *   estimate was made the new way: the new method put some pages at 0 with nothing else changed (13
 *   rival pages on local data, from 0.08 to 12.2 estimated visits), and a provider's change must not
 *   be what makes a page look idle (owner D4, TRAFFIC_ESTIMATE_CUTOVER.md §3 stage 4).
 *
 * "Connected" means a connection with data, not the "pick comparison searches" switch
 * (`useForSerpQuery`): that switch decides which search a page is compared on, not whether its clicks
 * are known.
 *
 * A page with traffic gets the Issues queue's own action for an unclustered page (assign a cluster)
 * and is never told to delete, rewrite-or-delete, or merge. A page with no traffic on record is told
 * to go only when another page of the site covers its searches (quality sprint 7 D: the competing-
 * pages engine, `competing-pages.ts`), and then the advice names that page and the shared searches:
 * "merge into <page>". Being in no cluster and idle is not a reason on its own: such a page gets the
 * cluster advice. Both the review surface and the agent export read these rules, so they cannot
 * disagree.
 *
 * A page whose job is not search traffic (`page-role.ts`: a menu or footer page, a customer-story
 * section) is never told to delete or merge either, traffic or not (quality sprint 5, workstream G).
 */

import { pageRoleIsNotSearch, pageRoleSentence, type PageRole } from './page-role';
import type { CompetingEvidenceSource, CompetingPair } from './competing-pages';
import { GSC_PRIMARY_QUERY_MIN_IMPRESSIONS } from './gsc-page-floor';
import { ESTIMATED_SEARCH_EVIDENCE_WARNING } from './estimated-evidence';

export type PageTrafficEvidence =
  | {
      source: 'search_console';
      /** Clicks in the summary window; 0 when Search Console has no row for the page. */
      clicks: number;
      /**
       * Impressions in the summary window; 0 when Search Console has no row for the page. Absent on
       * a record built before quality sprint 7 D: read as 0, the rule those records were judged by.
       */
      impressions?: number;
      windowDays: number;
      /** Last day of the window, `YYYY-MM-DD`, or null when the connection never said. */
      windowEnd: string | null;
    }
  | {
      source: 'estimate';
      /** The crawl's estimated visits/month, unrounded. */
      estimatedVisits: number;
      rankedKeywords: number;
      /**
       * The page's estimate in the last crawl captured before DataForSEO's 1 November 2026 change,
       * present only when the current estimate was made after it. Absent otherwise.
       */
      earlierEstimate?: { estimatedVisits: number; capturedOn: string } | null;
    }
  | {
      /** Not connected, and the page was never analysed: nothing is on record either way. */
      source: 'unmeasured';
    };

/** The page earns search traffic on the record the site has. Unmeasured counts as not proven idle. */
export function pageHasSearchTraffic(evidence: PageTrafficEvidence): boolean {
  if (evidence.source === 'search_console') return evidence.clicks > 0;
  if (evidence.source === 'estimate') {
    return (
      evidence.estimatedVisits > 0 ||
      evidence.rankedKeywords > 0 ||
      (evidence.earlierEstimate?.estimatedVisits ?? 0) > 0
    );
  }
  return false;
}

/**
 * Google shows the page often, clicks or not (Search Console connected): at least
 * `GSC_PRIMARY_QUERY_MIN_IMPRESSIONS` impressions in the summary window, the owner's floor at which
 * the product names a page's own Search Console search and compares it on that search. A page the
 * product compares on a measured search of its own is not idle, so it is never told to go.
 *
 * Why impressions and not clicks alone (quality sprint 7 D, frozen data of 2026-10-21): on
 * ManyRequests, 38 unclustered pages had no click in the 90 days; 24 had a Search Console row, with
 * 0 to 960 impressions. The two graded wrong in sprint 6's walk (substantive guides on the product's
 * core topic, one placed #4 in its stored results) had 578 and 526: 276 above the floor. Six pages
 * clear it (338 to 960), each with a search of its own; the nearest under it has 225 and is a
 * comparison page, which merge advice excludes anyway. SlideSpeak has no Search Console: its record
 * is ranked keywords, and any ranked keyword already counts as traffic.
 */
export function pageSeenInSearch(evidence: PageTrafficEvidence): boolean {
  if (evidence.source !== 'search_console') return false;
  return (evidence.impressions ?? 0) >= GSC_PRIMARY_QUERY_MIN_IMPRESSIONS;
}

/**
 * Delete or merge may be advised only on a measured page with no traffic on record and not seen in
 * search. On its own that is not enough: the unclustered advice also needs a page that covers the
 * searches (`unclusteredCoveringPage`).
 */
export function pageMayBeDeleted(evidence: PageTrafficEvidence): boolean {
  return evidence.source !== 'unmeasured' && !pageHasSearchTraffic(evidence) && !pageSeenInSearch(evidence);
}

export type UnclusteredPageRecommendation = 'merge' | 'assign_cluster' | 'keep';

/** At most this many shared searches are named in the merge advice (the engine keeps them all). */
export const UNCLUSTERED_COVER_SEARCHES_SHOWN = 3;

/**
 * The page an idle unclustered page can go into: another page of the site that competes with it
 * (`competing-pages.ts`, `competes`) and holds its searches. Kept small and plain so another card
 * can read the same searches.
 */
export interface UnclusteredCoveringPage {
  pageId: string;
  url: string;
  /** Where the pair's evidence comes from: measured (Search Console) or estimated. */
  source: CompetingEvidenceSource;
  /** The shared searches the other page holds, most visible first (`UNCLUSTERED_COVER_SEARCHES_SHOWN`). */
  searches: string[];
}

/**
 * The other page covers this page's searches: the pair competes, and the other page holds them:
 * Google answers this page's own comparison search with the other page on page one
 * (`ownSearchHeld`), or the other page is seen better placed on the pair's most visible contested
 * search. A pair whose two pages are translations of each other (`translation`) never qualifies: the
 * other page answers the same search in another language, for other readers.
 *
 * `pairs` are the page's pairs with the page as side `a` (`competingPairsForPage`). The strongest
 * covering pair wins: largest share of this page's demand on the contested searches, then the URL.
 */
export function unclusteredCoveringPage(
  pairs: readonly CompetingPair[],
  translation?: (otherPageId: string) => boolean
): UnclusteredCoveringPage | null {
  const heldSearches = (p: CompetingPair): string[] =>
    p.sharedSearches
      .filter((s) => s.contested && s.b.position !== null && (s.a.position === null || s.b.position < s.a.position))
      .map((s) => s.search);
  const covering = pairs.filter((p) => {
    if (!p.competes || p.exclusion) return false;
    if (translation?.(p.b.pageId)) return false;
    if (p.ownSearchHeld.a) return true;
    const top = p.sharedSearches.find((s) => s.contested);
    return Boolean(top && heldSearches(p)[0] === top.search);
  });
  if (!covering.length) return null;
  covering.sort(
    (x, y) => y.contestedShare.a - x.contestedShare.a || (x.b.url < y.b.url ? -1 : x.b.url > y.b.url ? 1 : 0)
  );
  const best = covering[0];
  const held = heldSearches(best);
  const searches = (held.length ? held : best.sharedSearches.filter((s) => s.contested).map((s) => s.search)).slice(
    0,
    UNCLUSTERED_COVER_SEARCHES_SHOWN
  );
  return { pageId: best.b.pageId, url: best.b.url, source: best.source, searches };
}

/**
 * An unclustered page sits outside the topic plan. That alone is not a reason to remove it: templates
 * and tools often sit outside the plan and still earn visits. So the advice is the queue's own
 * (assign a cluster), and a page is told to go only when nothing on record says it is visited or
 * seen AND another page covers its searches (`covering`): then it is "merge into <page>". A page with
 * nothing on record whose job is not search (`page-role.ts`) is kept: no clicks is what such a page
 * is expected to have. There is no bare "Delete" (quality sprint 7 D).
 */
export function unclusteredRecommendationFor(
  evidence: PageTrafficEvidence,
  role?: PageRole | null,
  covering?: UnclusteredCoveringPage | null
): UnclusteredPageRecommendation {
  if (!pageMayBeDeleted(evidence)) return 'assign_cluster';
  if (pageRoleIsNotSearch(role)) return 'keep';
  return covering ? 'merge' : 'assign_cluster';
}

/** A page's path for the advice line, or the URL itself when it does not parse. */
function pagePath(url: string): string {
  try {
    return new URL(url).pathname || url;
  } catch {
    return url;
  }
}

/**
 * The advice line. `covering` names the page on "merge"; without it (an Inventory column listing
 * rows by recommendation alone) the line stays generic.
 */
export function unclusteredRecommendationLabel(
  rec: UnclusteredPageRecommendation | string,
  covering?: UnclusteredCoveringPage | null
): string {
  if (rec === 'merge') {
    return covering
      ? `Merge into ${pagePath(covering.url)}, then redirect this page to it`
      : 'Merge into the page that covers its searches';
  }
  if (rec === 'assign_cluster') return 'Assign a cluster';
  if (rec === 'keep') return 'No change: not judged on search traffic';
  return rec;
}

const quoteList = (items: string[]): string => {
  const q = items.map((s) => `"${s}"`);
  if (q.length <= 1) return q.join('');
  return `${q.slice(0, -1).join(', ')} and ${q[q.length - 1]}`;
};

/**
 * Why the other page covers this one, in one sentence, and on estimates that they are estimates
 * (no Search Console: TECHNICAL_DEBT §0cj).
 */
export function unclusteredCoveringSentence(covering: UnclusteredCoveringPage): string {
  const path = pagePath(covering.url);
  const searches = covering.searches.length
    ? ` on ${covering.searches.length === 1 ? 'the search' : 'the searches'} they share: ${quoteList(covering.searches)}`
    : '';
  const lead = `${path} covers this page's searches: Google shows it ahead of this page${searches}. Move anything this page has that ${path} lacks before redirecting.`;
  return covering.source === 'estimated'
    ? `${lead} ${ESTIMATED_SEARCH_EVIDENCE_WARNING}`
    : lead;
}

/**
 * The record the unclustered advice rests on: on a kept page, why its job is not search first, then
 * its traffic record, so "no clicks" never reads as the reason to act; on a merge, the record and
 * then which page covers it and on what searches.
 */
export function unclusteredEvidenceSentenceFor(
  recommendation: UnclusteredPageRecommendation | string,
  evidence: PageTrafficEvidence,
  role?: PageRole | null,
  covering?: UnclusteredCoveringPage | null
): string {
  const record = pageTrafficEvidenceSentence(evidence);
  if (recommendation === 'merge' && covering) return `${record} ${unclusteredCoveringSentence(covering)}`;
  const why = recommendation === 'keep' ? pageRoleSentence(role) : null;
  return why ? `${why} ${record}` : record;
}

const fmtVisits = (n: number): string => (n >= 10 ? String(Math.round(n)) : String(Math.round(n * 10) / 10));

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

function dayMonth(isoDay: string): string {
  const [, m, d] = isoDay.split('-').map(Number);
  return m >= 1 && m <= 12 && d ? `${d} ${MONTHS[m - 1]}` : isoDay;
}

/** What the page's traffic record says, in one sentence a client can read. */
export function pageTrafficEvidenceSentence(evidence: PageTrafficEvidence): string {
  if (evidence.source === 'search_console') {
    const window = `in the ${evidence.windowDays} days` + (evidence.windowEnd ? ` to ${evidence.windowEnd}` : '');
    if (evidence.clicks <= 0) {
      const impressions = evidence.impressions ?? 0;
      if (impressions <= 0) return `No Search Console clicks ${window}.`;
      return `No Search Console clicks ${window}, from ${impressions} ${impressions === 1 ? 'impression' : 'impressions'}.`;
    }
    return `${evidence.clicks} Search Console ${evidence.clicks === 1 ? 'click' : 'clicks'} ${window}.`;
  }
  if (evidence.source === 'estimate') {
    if (!pageHasSearchTraffic(evidence)) {
      return 'No estimated visits and no ranked keywords on record (Search Console is not connected).';
    }
    const earlier = evidence.earlierEstimate;
    if (evidence.estimatedVisits <= 0 && evidence.rankedKeywords <= 0 && earlier && earlier.estimatedVisits > 0) {
      return (
        "No estimated visits under DataForSEO's current method and no ranked keywords; " +
        `about ${fmtVisits(earlier.estimatedVisits)} estimated visits/month in the crawl of ${dayMonth(earlier.capturedOn)}, ` +
        "before DataForSEO changed how it estimates traffic on 1 November (our crawl's estimate, not Search Console)."
      );
    }
    const parts: string[] = [];
    if (evidence.estimatedVisits > 0) parts.push(`about ${fmtVisits(evidence.estimatedVisits)} estimated visits/month`);
    if (evidence.rankedKeywords > 0) {
      parts.push(`${evidence.rankedKeywords} ranked ${evidence.rankedKeywords === 1 ? 'keyword' : 'keywords'}`);
    }
    const joined = parts.join(' and ');
    return `${joined.charAt(0).toUpperCase()}${joined.slice(1)} (our crawl's estimate, not Search Console).`;
  }
  return 'This page has not been analysed, so there is no traffic record for it.';
}

